// ═══════════════════════════════════════════════════════════
// ARAMS UI — Progress Bar Component
// ═══════════════════════════════════════════════════════════

import { cva, type VariantProps } from 'class-variance-authority';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

const progressBarVariants = cva('h-full rounded-full', {
  variants: {
    color: {
      brand: 'bg-brand-500',
      success: 'bg-success',
      danger: 'bg-danger',
      warning: 'bg-warning',
      info: 'bg-info',
      gradient: 'bg-gradient-to-r from-brand-500 to-purple-500',
    },
  },
  defaultVariants: {
    color: 'brand',
  },
});

export interface ProgressBarProps extends VariantProps<typeof progressBarVariants> {
  value: number;
  max?: number;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  animated?: boolean;
}

export function ProgressBar({
  value,
  max = 100,
  color,
  showLabel,
  size = 'md',
  className,
  animated = true,
}: ProgressBarProps) {
  const pct = Math.min(Math.round((value / max) * 100), 100);
  const height = size === 'sm' ? 'h-1.5' : size === 'lg' ? 'h-3' : 'h-2';

  return (
    <div className={cn('space-y-1', className)}>
      <div className={cn(height, 'bg-surface-800 rounded-full overflow-hidden')}>
        <motion.div
          initial={animated ? { width: 0 } : false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: animated ? 0.6 : 0, ease: 'easeOut' }}
          className={cn(progressBarVariants({ color }))}
        />
      </div>
      {showLabel && <span className="text-xs text-surface-400">{pct}%</span>}
    </div>
  );
}

ProgressBar.displayName = 'ProgressBar';
